import React, { useRef, useState, useEffect } from 'react'
import Breadcrumb from '../../../core/common/Breadcrumb/breadcrumb'
import { Link, useLocation } from 'react-router-dom'
import { all_routes } from '../../router/all_routes'
import ImageWithBasePath from '../../../core/common/imageWithBasePath'
import { useContentDetail } from '../hooks/useContentDetail'
import { useAuth } from '../../../provider/AuthProvider'
import TransferManual from '../../../core/common/modal/transferManualModal'
import * as bootstrap from 'bootstrap/dist/js/bootstrap.bundle'
import { createManualTransaction, CBTData, Course } from '../../../api/courses'

const isRegularCourseDetail = (
  value: CBTData | Course,
): value is Course =>
  'topics' in value || 'cross_sells' in value || 'benefits' in value

const CourseCheckout = () => {
  const route = all_routes
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const courseId = searchParams.get('id') || ''

  // Get user data from auth context
  const { user, isAuthenticated } = useAuth()

  // Fetch course detail
  const { course, isLoading, isError, error } = useContentDetail({
    id: courseId,
  })

  // Refs untuk input
  const fullNameRef = useRef<HTMLInputElement>(null)
  const emailRef = useRef<HTMLInputElement>(null)
  const phoneRef = useRef<HTMLInputElement>(null)
  const modalRef = useRef<HTMLDivElement>(null)
  const [errorMsg, setErrorMsg] = useState({
    fullName: '',
    email: '',
    phone: '',
  })
  // const [voucher, setVoucher] = useState('')
  // const [voucherStatus, setVoucherStatus] = useState<
  //   'idle' | 'loading' | 'valid' | 'invalid'
  // >('idle')
  const [discount, setDiscount] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isFormAutoFilled, setIsFormAutoFilled] = useState(false)

  // Form values state for better control
  const [formValues, setFormValues] = useState({
    fullName: '',
    email: '',
    phone: '',
  })

  // State untuk courses dan crossSell agar bisa dinamis
  const [courses, setCourses] = useState([])
  const [crossSell, setCrossSell] = useState<any[]>([])
  const [isCrossSellInitialized, setIsCrossSellInitialized] = useState(false)

  // Calculate discount based on course data
  useEffect(() => {
    if (course && course.discount && course.discount_type) {
      const discountValue = parseFloat(course.discount)
      const subTotal = courses.reduce((sum, c) => sum + c.price, 0)

      if (course.discount_type === 'PERCENTAGE') {
        // Calculate percentage discount
        const discountAmount = (subTotal * discountValue) / 100
        setDiscount(discountAmount)
      } else if (course.discount_type === 'NOMINAL') {
        // Use nominal discount directly
        setDiscount(discountValue)
      } else {
        setDiscount(0)
      }
    } else {
      setDiscount(0)
    }
  }, [course, courses])

  // Isi courses dan crossSell ketika course sudah tersedia
  useEffect(() => {
    if (course) {
      // Set main course
      if (courses.length === 0) {
        setCourses([
          {
            id: course.id,
            title: course.title,
            price: Number(course.price),
            image: course.image || '/assets/img/course/course-01.jpg',
            link: route.courseDetails + `?id=${course.id}`,
          },
        ])
      }
      // Set crossSell dari API hanya saat pertama kali load course
      if (
        isRegularCourseDetail(course) &&
        Array.isArray(course.cross_sells) &&
        !isCrossSellInitialized
      ) {
        setCrossSell(
          course.cross_sells.map((item: any) => {
            const cross = item.cross_course || {}
            return {
              id: cross.id,
              title: cross.title,
              price: Number(item.price),
              image: cross.image || '/assets/img/course/course-01.jpg',
              link: route.courseDetails + `?id=${cross.id}`,
              note: item.note,
              status: cross.status,
            }
          }),
        )
        setIsCrossSellInitialized(true)
      }
    }
  }, [course, route.courseDetails, courses.length, isCrossSellInitialized])

  // Reset crossSell jika ganti courseId
  useEffect(() => {
    setIsCrossSellInitialized(false)
    setCrossSell([])
  }, [courseId])

  // Auto-fill form when user is logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      // Set form values directly using state
      const newFormValues = {
        fullName: user.fullname || user.name || '',
        email: user.email || '',
        phone: user.phone_number || user.phone || '',
      }

      setFormValues(newFormValues)

      // Clear any existing error messages
      setErrorMsg({ fullName: '', email: '', phone: '' })

      // Mark form as auto-filled
      setIsFormAutoFilled(true)
    }
  }, [isAuthenticated, user])

  const orderData = {
    courses,
    crossSell,
    get subTotal() {
      return this.courses.reduce((sum, c) => sum + c.price, 0)
    },
    discount: discount,
    get total() {
      return Math.max(this.subTotal - this.discount, 0)
    },
  }

  const isZeroTotal = orderData.total <= 0

  // Handler untuk menambah cross sell ke order
  const handleAddCrossSellToOrder = (item: typeof crossSell[0]) => {
    setCourses((prev) => [...prev, item])
    setCrossSell((prev) => prev.filter((c) => c.id !== item.id))
  }

  // Handler untuk menghapus course dari order
  const handleRemoveCourseFromOrder = (id: number) => {
    // Main course tidak bisa dihapus
    if (courses.length === 0 || id === courses[0].id) return
    // Hanya cross sell yang bisa dikembalikan
    const removed = courses.find((c) => c.id === id)
    if (removed && crossSell.every((c) => c.id !== id)) {
      setCrossSell((prev) => [...prev, removed])
    }
    setCourses((prev) => prev.filter((c) => c.id !== id))
  }

  // Mock voucher validation
  // const handleCheckVoucher = async () => {
  //   setVoucherStatus('loading')
  //   setDiscount(0)
  //   // Simulasi loading
  //   await new Promise((resolve) => setTimeout(resolve, 1200))
  //   if (voucher.trim().toLowerCase() === 'diskon50') {
  //     setVoucherStatus('valid')
  //     setDiscount(50000)
  //   } else {
  //     setVoucherStatus('invalid')
  //     setDiscount(0)
  //   }
  // }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // Validate phone number
    const phoneValidation = validatePhoneNumber(formValues.phone)

    if (!e.currentTarget.checkValidity() || phoneValidation) {
      e.stopPropagation()
      setErrorMsg({
        fullName: fullNameRef.current?.validationMessage || '',
        email: emailRef.current?.validationMessage || '',
        phone: phoneValidation || phoneRef.current?.validationMessage || '',
      })
      // Tampilkan pesan error browser
      e.currentTarget.reportValidity()
    } else {
      setErrorMsg({ fullName: '', email: '', phone: '' })
      setSubmitError(null)
      setIsSubmitting(true)
      try {
        // Pastikan course dan total sudah ada
        if (!course) throw new Error('Course data not loaded')
        const fullname = formValues.fullName
        const email = formValues.email
        const phone_number = formValues.phone
        const total = orderData.total
        // Calculate discounted price for each course based on discount type
        const courseData = courses.map((c) => {
          let discountedPrice = c.price

          if (course?.discount && course?.discount_type) {
            const discountValue = parseFloat(course.discount)

            if (course.discount_type === 'PERCENTAGE') {
              // Apply percentage discount directly to each course
              discountedPrice = c.price - (c.price * discountValue) / 100
            } else if (course.discount_type === 'NOMINAL') {
              // Apply nominal discount proportionally to each course
              const subTotal = courses.reduce(
                (sum, courseItem) => sum + courseItem.price,
                0,
              )
              const courseRatio = c.price / subTotal
              discountedPrice = c.price - discountValue * courseRatio
            }
          }

          return {
            course_id: c.id,
            price: Math.max(discountedPrice, 0), // Ensure price doesn't go below 0
          }
        })
        const { redirect_url } = await import('../../../api/transaction').then(
          (m) =>
            m.postPaymentUrl({
              fullname,
              email,
              phone_number,
              courses: courseData,
              total,
            }),
        )
        // Redirect ke payment url
        window.location.href = redirect_url
      } catch (err) {
        setSubmitError(err?.message || 'Gagal melakukan checkout. Coba lagi.')
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  // Phone number validation function
  const validatePhoneNumber = (phone: string): string => {
    // Remove any non-digit characters
    const cleanPhone = phone.replace(/\D/g, '')

    // Check if it starts with 62
    if (!cleanPhone.startsWith('62')) {
      return 'Nomor telepon harus diawali dengan 62'
    }

    // Check minimum length (62 + 8 digits = 10 total)
    if (cleanPhone.length < 10) {
      return 'Nomor telepon minimal 10 digit'
    }

    return ''
  }

  // Hapus pesan error saat user mengubah input
  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = e.target
    setErrorMsg((prev) => ({ ...prev, [name]: '' }))

    // If user manually edits the form, mark it as no longer auto-filled
    if (isFormAutoFilled) {
      setIsFormAutoFilled(false)
    }
  }

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading course data...</p>
      </div>
    )
  }
  if (isError || !course) {
    return (
      <div className="text-center py-5 text-danger">
        <p>Error loading course: {error?.message || 'Unknown error'}</p>
        <p>Please try again later.</p>
      </div>
    )
  }

  const handleTransferClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()

    if (isZeroTotal) {
      return
    }

    const form = document.querySelector('form')
    if (!form) return

    const isValid = form.checkValidity()
    const phoneValidation = validatePhoneNumber(formValues.phone)

    if (!isValid || phoneValidation) {
      form.reportValidity()
      setErrorMsg({
        fullName: fullNameRef.current?.validationMessage || '',
        email: emailRef.current?.validationMessage || '',
        phone: phoneValidation || phoneRef.current?.validationMessage || '',
      })
      return
    }

    try {
      setIsSubmitting(true)
      setSubmitError(null)

      if (!course) throw new Error('Course data not loaded')

      const fullname = formValues.fullName
      const email = formValues.email
      const phone_number = formValues.phone
      const total = orderData.total

      const courseData = courses.map((c) => {
        let discountedPrice = c.price
        if (course.discount && course.discount_type) {
          const discountValue = parseFloat(course.discount)
          if (course.discount_type === 'PERCENTAGE') {
            discountedPrice = c.price - (c.price * discountValue) / 100
          } else if (course.discount_type === 'NOMINAL') {
            const subTotal = courses.reduce(
              (sum, courseItem) => sum + courseItem.price,
              0,
            )
            const ratio = c.price / subTotal
            discountedPrice = c.price - discountValue * ratio
          }
        }
        return { course_id: c.id, price: Math.max(discountedPrice, 0) }
      })

      const res = await createManualTransaction({
        fullname,
        email,
        phone_number,
        courses: courseData,
        total,
      })

      if (res && modalRef.current && window.bootstrap) {
        const modal = window.bootstrap.Modal.getOrCreateInstance(modalRef.current)
        modal.show()
      }
    } catch (err: any) {
      setSubmitError(err?.response.data.message || 'Gagal membuat transaksi manual.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmTransfer = () => {
    const adminNumber = '6285213489432'
    const courseList = courses.map((c) => `- ${c.title}`).join('\n')
    const message = `
Halo Admin PT. Jadi Praktisi Indonesia 👋

Saya ingin konfirmasi pembayaran manual.

Nama: ${formValues.fullName}
Email: ${formValues.email}

Kursus yang dibeli:
${courseList}

Mohon untuk segera dikonfirmasi ya 🙏
  `.trim()

    const encodedMessage = encodeURIComponent(message)
    const waUrl = `https://wa.me/${adminNumber}?text=${encodedMessage}`

    window.location.href = waUrl
  }

  return (
    <>
      <Breadcrumb title="Checkout" />
      <>
        {/* Checkout */}
        <div className="content">
          <div className="container">
            <div className="checkout-content">
              <div className="row">
                <div className="col-lg-8">
                  {orderData.crossSell.length > 0 && (<>
                    <div className="checkout-item-2 mb-4">
                      <div className="pb-3 border-bottom mb-3">
                        <h5 className="mb-0">Rekomendasi Produk Serupa</h5>
                      </div>
                      <div className="checkout-item-3 bg-light p-3 rounded-3 mb-3">
                        {orderData.crossSell.map((course) => (
                          <div className="row row-gap-2 mb-3" key={course.id}>
                            <div className="col-md-12 d-flex align-items-center">
                              <div className="order-img flex-shrink-0 me-3 d-flex align-items-center">
                                <ImageWithBasePath
                                  src={''}
                                  srcApi={course.image}
                                  alt="img"
                                  className="img-fluid h-100 w-100"
                                />
                              </div>
                              <div className="flex-grow-1">
                                <h6 className="mb-2">
                                  <Link to={course.link}>{course.title}</Link>
                                  {course.status === 'UNPUBLISHED' && (
                                    <span className="badge bg-warning ms-2">
                                      Unpublished
                                    </span>
                                  )}
                                </h6>
                                {course.note && (
                                  <div className="text-muted small mb-1">
                                    {course.note}
                                  </div>
                                )}
                                <div className="fw-bold">
                                  Rp {course.price.toLocaleString('id-ID')}
                                </div>
                              </div>
                              <button
                                className="btn btn-primary ms-3"
                                onClick={() =>
                                  handleAddCrossSellToOrder(course)
                                }
                              >
                                <i className="fas fa-plus"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div></>)}
                  <div className="checkout-item-1">
                    <div className="border-bottom pb-3 mb-3">
                      <h5>Data Pembeli</h5>
                      {isAuthenticated && user && isFormAutoFilled && (
                        <div className="alert alert-info alert-sm mb-0 mt-2">
                          <i className="isax isax-info-circle me-2"></i>
                          Form telah diisi otomatis berdasarkan data akun Anda.
                          Anda dapat mengubah data jika diperlukan.
                        </div>
                      )}
                    </div>
                    <form
                      id="checkoutForm"
                      noValidate
                      onSubmit={handleSubmit}
                      autoComplete="off"
                    >
                      <div className="row">
                        <div className="col-md-12">
                          <div className="input-block">
                            <label htmlFor="fullName" className="form-label">
                              Nama Lengkap
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              id="fullName"
                              name="fullName"
                              required
                              autoComplete="name"
                              placeholder="Nama Lengkap"
                              ref={fullNameRef}
                              value={formValues.fullName}
                              onChange={(e) => {
                                setFormValues((prev) => ({
                                  ...prev,
                                  fullName: e.target.value,
                                }))
                                handleInput(e)
                              }}
                            />
                            {errorMsg.fullName && (
                              <div className="text-danger small mt-1">
                                {errorMsg.fullName}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="input-block">
                            <label htmlFor="email" className="form-label">
                              Email
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <input
                              type="email"
                              className="form-control"
                              id="email"
                              name="email"
                              required
                              autoComplete="email"
                              placeholder="Email"
                              ref={emailRef}
                              value={formValues.email}
                              disabled={isAuthenticated}
                              onChange={(e) => {
                                setFormValues((prev) => ({
                                  ...prev,
                                  email: e.target.value,
                                }))
                                handleInput(e)
                              }}
                            />
                            {errorMsg.email && (
                              <div className="text-danger small mt-1">
                                {errorMsg.email}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-12">
                          <div className="input-block">
                            <label htmlFor="phone" className="form-label">
                              Nomor Whatsapp
                              <span className="text-danger ms-1">*</span>
                            </label>
                            <input
                              type="tel"
                              className="form-control"
                              id="phone"
                              name="phone"
                              autoComplete="tel"
                              required
                              placeholder="62858xxxxx (minimal 10 digit)"
                              ref={phoneRef}
                              value={formValues.phone}
                              onChange={(e) => {
                                setFormValues((prev) => ({
                                  ...prev,
                                  phone: e.target.value,
                                }))
                                handleInput(e)
                              }}
                            />
                            {errorMsg.phone && (
                              <div className="text-danger small mt-1">
                                {errorMsg.phone}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="checkout-item-2">
                    <div className="pb-3 border-bottom mb-3">
                      <h5 className="mb-0">Detail Pembelian</h5>
                    </div>
                    <div className="checkout-item-3 bg-light p-3 rounded-3 border mb-3">
                      {orderData.courses.length === 0 ? (
                        <div className="text-muted">
                          Tidak ada course dalam order.
                        </div>
                      ) : (
                        orderData.courses.map((course) => (
                          <div className="row row-gap-2 mb-3" key={course.id}>
                            <div className="col-md-12 d-flex align-items-center">
                              <div className="order-img flex-shrink-0 me-3 position-relative">
                                <ImageWithBasePath
                                  src={''}
                                  srcApi={course.image}
                                  alt="img"
                                  className="img-fluid h-100 w-100"
                                />
                              </div>
                              <div className="d-flex flex-grow-1 align-items-center justify-content-between">
                                <div>
                                  <h6 className="mb-2">
                                    <Link to={course.link}>{course.title}</Link>
                                  </h6>
                                  <h6 className="text-secondary">
                                    Rp{course.price.toLocaleString('id-ID')}
                                  </h6>
                                </div>
                                {orderData.courses.length > 1 &&
                                  course.id !== orderData.courses[0].id && (
                                    <button
                                      type="button"
                                      className="btn p-1 rounded-circle ms-3"
                                      title="Hapus dari order"
                                      style={{
                                        height: 32,
                                        width: 32,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                      onClick={() =>
                                        handleRemoveCourseFromOrder(course.id)
                                      }
                                    >
                                      <i className="isax isax-trash" />
                                    </button>
                                  )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    {/* Voucher Input */}
                    {/* <div className="mb-3">
                      <label htmlFor="voucher" className="form-label mb-1">
                        Voucher
                      </label>
                      <div className="d-flex align-items-center position-relative">
                        <span
                          className="input-icon-addon position-absolute"
                          style={{ left: 12, zIndex: 2 }}
                        >
                          <i className="isax isax-ticket-discount" />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-md ps-5"
                          id="voucher"
                          placeholder="Masukkan kode voucher"
                          value={voucher}
                          onChange={(e) => {
                            setVoucher(e.target.value)
                            setVoucherStatus('idle')
                          }}
                          disabled={voucherStatus === 'loading'}
                          style={{ height: 40, minWidth: 0 }}
                        />
                        <button
                          type="button"
                          className="btn btn-primary rounded-pill ms-2 d-flex align-items-center"
                          style={{ height: 40, whiteSpace: 'nowrap' }}
                          onClick={handleCheckVoucher}
                          disabled={
                            voucherStatus === 'loading' || !voucher.trim()
                          }
                        >
                          {voucherStatus === 'loading' ? (
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            <i className="isax isax-search-normal-1 me-1" />
                          )}
                          Check
                        </button>
                      </div>
                      {voucherStatus === 'valid' && (
                        <div className="text-success small mt-1">
                          <i className="isax isax-tick-circle me-1" />
                          Voucher valid! Diskon Rp50.000 diterapkan.
                        </div>
                      )}
                      {voucherStatus === 'invalid' && (
                        <div className="text-danger small mt-1">
                          <i className="isax isax-close-circle me-1" />
                          Voucher tidak valid.
                        </div>
                      )}
                    </div> */}
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <p className="mb-0">Sub Total</p>
                      <p className="text-gray-9 fw-medium mb-0">
                        Rp{orderData.subTotal.toLocaleString('id-ID')}
                      </p>
                    </div>
                    {discount > 0 && course?.discount_type && (
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <p className="mb-0">
                          Discount
                          {course.discount_type === 'PERCENTAGE' && (
                            <span className="text-muted ms-1">
                              ({course.discount}%)
                            </span>
                          )}
                        </p>
                        <p className="text-success fw-medium mb-0">
                          -Rp{discount.toLocaleString('id-ID')}
                        </p>
                      </div>
                    )}
                    <div className="total d-flex align-items-center justify-content-between">
                      <h6 className="mb-0">Total</h6>
                      <h4 className="mb-0">
                        Rp{orderData.total.toLocaleString('id-ID')}
                      </h4>
                    </div>
                    <div className="total d-flex align-items-center justify-content-end mt-5">
                      <div className="w-100">
                        {!isZeroTotal && (
                          <button
                            type="button"
                            form="checkoutForm"
                            className="btn btn-lg btn-info rounded-pill self-center w-100 mb-2"
                            disabled={isSubmitting}
                            onClick={handleTransferClick}
                          >
                            {isSubmitting ? (
                              <span
                                className="spinner-border spinner-border-sm me-1"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            ) : (
                              'Bayar Manual Transfer'
                            )}
                          </button>
                        )}
                        <button
                          type="submit"
                          form="checkoutForm"
                          className="btn btn-lg btn-success rounded-pill self-center w-100 mb-2"
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          ) : (
                            'Bayar QR/VA Midtrans'
                          )}
                        </button>
                      </div>
                    </div>
                    {submitError && (
                      <div className="text-danger small mt-2 ms-2">
                        {submitError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* /Checkout */}

        {/* Modal */}
        <TransferManual ref={modalRef} handleConfirmTransfer={handleConfirmTransfer} />
      </>
    </>
  )
}

export default CourseCheckout
